import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  CheckCircle2,
  Copy,
  ExternalLink,
  Eye,
  FileText,
  History,
  Layers,
  RotateCcw,
  Sparkles,
  Undo2,
  XCircle,
} from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import AIBadge from '../../components/AIBadge';
import InfoTip from '../../components/InfoTip';
import ErrorBanner from '../../components/ErrorBanner';
import Dialog from '../../components/Dialog';
import { SkeletonTable } from '../../components/Skeleton';
import { toast } from '../../lib/toast';
import { formatDate, formatRelative } from '../../lib/format';
import {
  extractJsonLd,
  extractFaqHtml,
  extractAnswerCapsules,
} from '../../lib/optimizedContent';
import RewriterPromptPicker, {
  pickerValueToBody,
} from '../../components/RewriterPromptPicker';

export default function ContentTab({ domain }) {
  const qc = useQueryClient();
  const [pageUrl, setPageUrl] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [bulkUrls, setBulkUrls] = useState('');
  const [bulkAuthor, setBulkAuthor] = useState('');
  const [bulkResults, setBulkResults] = useState(null);
  const [error, setError] = useState('');
  const [selectedPage, setSelectedPage] = useState(null);
  const [promptValue, setPromptValue] = useState({ kind: 'default' });
  const [bulkPromptValue, setBulkPromptValue] = useState({ kind: 'default' });

  const { data, isLoading } = useQuery({
    queryKey: ['pages', domain._id],
    queryFn: () => api.get(`/api/domains/${domain._id}/pages`).then((r) => r.data.pages),
  });

  const rewriteMutation = useMutation({
    mutationFn: (payload) =>
      api.post(`/api/domains/${domain._id}/pages/rewrite`, payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pages', domain._id] });
      setPageUrl('');
      setError('');
    },
    onError: (err) => setError(err.response?.data?.error || 'Rewrite failed'),
  });

  const bulkMutation = useMutation({
    mutationFn: (payload) =>
      api.post(`/api/domains/${domain._id}/pages/bulk-rewrite`, payload).then((r) => r.data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['pages', domain._id] });
      setBulkResults(data);
    },
    onError: (err) => setError(err.response?.data?.error || 'Bulk rewrite failed'),
  });

  const revertMutation = useMutation({
    mutationFn: (pageId) =>
      api.post(`/api/domains/${domain._id}/pages/${pageId}/revert`).then((r) => r.data.page),
    onSuccess: (page) => {
      qc.invalidateQueries({ queryKey: ['pages', domain._id] });
      setSelectedPage(page);
      toast.success('Reverted to original HTML');
    },
    onError: (err) => toast.error(err, 'Could not revert page'),
  });

  const restoreVersionMutation = useMutation({
    mutationFn: ({ pageId, content, label }) =>
      api
        .put(`/api/domains/${domain._id}/pages/${pageId}`, {
          optimized_content: content,
          label: label || 'restored',
        })
        .then((r) => r.data.page),
    onSuccess: (page) => {
      qc.invalidateQueries({ queryKey: ['pages', domain._id] });
      setSelectedPage(page);
      toast.success('Version restored');
    },
    onError: (err) => toast.error(err, 'Could not restore version'),
  });

  const onRewrite = (e) => {
    e.preventDefault();
    setError('');
    rewriteMutation.mutate({
      pageUrl,
      authorName: authorName || undefined,
      ...pickerValueToBody(promptValue),
    });
  };

  const onBulkRewrite = (e) => {
    e.preventDefault();
    setError('');
    setBulkResults(null);
    const pageUrls = bulkUrls
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!pageUrls.length) return;
    bulkMutation.mutate({
      pageUrls,
      authorName: bulkAuthor || undefined,
      ...pickerValueToBody(bulkPromptValue),
    });
  };

  const loadPageDetail = async (pageId) => {
    const { data } = await api.get(`/api/domains/${domain._id}/pages/${pageId}`);
    setSelectedPage(data.page);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        variant="gradient"
        icon={Sparkles}
        eyebrow="Content"
        title="AI content optimizer"
        description="Rewrite existing pages so AI engines can quote them confidently. Each rewrite adds FAQs, Answer Capsules, schema markup, and freshness signals — without changing what your page actually says."
        help={{
          title: 'What changes after a rewrite?',
          body:
            'We keep your original copy and add clear H2/H3 structure, FAQ blocks for common questions, short Answer Capsules (2–3 sentence summaries AI engines love to quote), and JSON-LD schema. Your original HTML is always recoverable from version history.',
        }}
      />
      <div className="card">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Sparkles className="w-4 h-4" />
            </span>
            Rewrite a page with AI
            <AIBadge label="AI" />
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          We fetch the URL, restructure the content for AI citation (FAQs,{' '}
          <span className="inline-flex items-center gap-1">
            Answer Capsules
            <InfoTip
              title="Answer Capsule"
              body="A 2–3 sentence direct answer to a common question, placed near the top of your page. AI engines preferentially quote these because they fit chat-style responses."
            />
          </span>
          , schema), and store both the original and optimized HTML so you can revert anytime.
        </p>
        <form onSubmit={onRewrite} className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input
            className="input md:col-span-2"
            type="url"
            placeholder={`https://${domain.domain}/your-article`}
            value={pageUrl}
            required
            onChange={(e) => setPageUrl(e.target.value)}
          />
          <input
            className="input"
            placeholder="Author name (optional)"
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
          />
          <div className="md:col-span-3">
            <RewriterPromptPicker value={promptValue} onChange={setPromptValue} />
          </div>
          <button
            className="btn-primary md:col-span-3"
            disabled={rewriteMutation.isPending}
          >
            {rewriteMutation.isPending ? (
              <>
                <Activity className="w-4 h-4 animate-pulse" /> Rewriting (this can take 30-60s)…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" /> Rewrite with AI
              </>
            )}
          </button>
        </form>
        {error && (
          <div className="mt-3">
            <ErrorBanner
              title="Rewrite failed"
              body={error}
              onDismiss={() => setError('')}
            />
          </div>
        )}
      </div>

      <details className="card">
        <summary className="cursor-pointer font-semibold text-base flex items-center gap-2">
          <Layers className="w-4 h-4 text-brand-600" /> Bulk rewrite (up to 10 URLs)
        </summary>
        <p className="text-sm text-gray-600 mt-2 mb-3">
          Processed sequentially — expect 30–60s per page. Stay on this tab; closing it will
          abort the job (the URLs already finished are saved).
        </p>
        <form onSubmit={onBulkRewrite} className="space-y-2">
          <textarea
            className="input min-h-[120px] text-xs"
            placeholder={`https://${domain.domain}/article-1\nhttps://${domain.domain}/article-2`}
            value={bulkUrls}
            onChange={(e) => setBulkUrls(e.target.value)}
          />
          <input
            className="input"
            placeholder="Author name (optional, applies to all)"
            value={bulkAuthor}
            onChange={(e) => setBulkAuthor(e.target.value)}
          />
          <RewriterPromptPicker value={bulkPromptValue} onChange={setBulkPromptValue} />
          <button className="btn-primary w-full" disabled={bulkMutation.isPending}>
            {bulkMutation.isPending ? (
              <>
                <Activity className="w-4 h-4 animate-pulse" /> Processing… (may take several minutes)
              </>
            ) : (
              <>
                <Layers className="w-4 h-4" /> Rewrite all
              </>
            )}
          </button>
        </form>
        {bulkResults && (
          <div className="mt-3 text-sm">
            <div className="font-medium">
              {bulkResults.totalSucceeded}/{bulkResults.totalRequested} succeeded · est. $
              {bulkResults.estimatedCost.toFixed(4)}
            </div>
            <ul className="mt-2 divide-y divide-gray-50 text-xs">
              {bulkResults.results.map((r, i) => (
                <li key={i} className="py-1.5 flex justify-between gap-3">
                  <span className="truncate flex-1">{r.url}</span>
                  {r.success ? (
                    <span className="text-emerald-700 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {r.enhancements?.faqs} FAQs,{' '}
                      {r.enhancements?.answerCapsules} caps
                    </span>
                  ) : (
                    <span className="text-red-700 flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> {r.error}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </details>

      <div className="card">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <FileText className="w-4 h-4" />
            </span>
            Tracked pages
          </div>
        </div>
        {isLoading ? (
          <SkeletonTable rows={5} cols={6} wrap={false} />
        ) : !data?.length ? (
          <EmptyState
            icon={FileText}
            title="No pages yet"
            body="Rewrite your first page above, or run an Audit to populate this list from your sitemap."
          />
        ) : (
          <div className="overflow-x-auto scroll-soft">
            <table className="data-table">
              <thead>
                <tr>
                  <th>URL</th>
                  <th className="text-right">Score</th>
                  <th className="text-right">FAQs</th>
                  <th className="text-right">Capsules</th>
                  <th className="text-right">Last rewritten</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.map((p) => (
                  <tr key={p._id}>
                    <td className="truncate max-w-[24rem] text-ink-800">{p.url}</td>
                    <td className="text-right font-semibold">{p.audit_score ?? '—'}</td>
                    <td className="text-right text-ink-400">{p.enhancements?.faqs ?? 0}</td>
                    <td className="text-right text-ink-400">
                      {p.enhancements?.answerCapsules ?? 0}
                    </td>
                    <td className="text-right text-ink-400 whitespace-nowrap">
                      {p.last_rewritten ? formatDate(p.last_rewritten) : '—'}
                    </td>
                    <td className="text-right">
                      <button
                        className="btn-icon"
                        title="View details"
                        onClick={() => loadPageDetail(p._id)}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedPage && (
        <Dialog
          open
          onClose={() => setSelectedPage(null)}
          title={selectedPage.title || selectedPage.url}
          icon={FileText}
          size="xl"
          footer={
            selectedPage.optimized_content ? (
              <button
                className="btn-secondary"
                onClick={() => {
                  if (
                    window.confirm(
                      'Revert to the original (un-optimized) HTML? The current optimized version stays in version history.',
                    )
                  ) {
                    revertMutation.mutate(selectedPage._id);
                  }
                }}
              >
                <Undo2 className="w-4 h-4" /> Revert to original
              </button>
            ) : null
          }
        >
          <a
            href={selectedPage.url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-brand-600 hover:underline break-all inline-flex items-center gap-1 mb-4"
          >
            {selectedPage.url} <ExternalLink className="w-3 h-3" />
          </a>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-ink-400 mb-1.5 font-semibold">
                Original (truncated)
              </div>
              <pre className="text-xs bg-gray-50 border border-gray-100 rounded-xl p-3 max-h-80 overflow-auto whitespace-pre-wrap scroll-soft">
                {(selectedPage.original_content || '').slice(0, 4000) || '(none)'}
              </pre>
            </div>
            <div>
              {(() => {
                const jsonLd = extractJsonLd(selectedPage.optimized_content);
                const faq = extractFaqHtml(selectedPage.optimized_content);
                const caps = extractAnswerCapsules(selectedPage.optimized_content);

                const copyText = (text, label) => async () => {
                  if (!text) return;
                  try {
                    await navigator.clipboard.writeText(text);
                    toast.success(`Copied ${label} to clipboard`);
                  } catch (err) {
                    toast.error(err, 'Could not copy to clipboard');
                  }
                };

                const jsonLdLabel = (() => {
                  const named = jsonLd.types.length ? ` (${jsonLd.types.join(', ')})` : '';
                  const noun = jsonLd.count === 1 ? 'script' : 'scripts';
                  return `${jsonLd.count} JSON-LD ${noun}${named}`;
                })();
                const faqLabel = `FAQ section with ${faq.count} Q&A ${faq.count === 1 ? 'pair' : 'pairs'}`;
                const capsLabel = `${caps.count} Answer Capsule${caps.count === 1 ? '' : 's'}`;

                const hasAny = jsonLd.count > 0 || faq.count > 0 || caps.count > 0;

                return (
                  <>
                    <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                      <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
                        Optimized (truncated)
                      </div>
                      {hasAny && (
                        <div className="flex flex-wrap gap-1.5">
                          {jsonLd.count > 0 && (
                            <button
                              type="button"
                              onClick={copyText(jsonLd.scriptsText, jsonLdLabel)}
                              className="text-xs inline-flex items-center gap-1 text-emerald-800 hover:text-emerald-900 bg-emerald-100 hover:bg-emerald-200 px-2 py-1 rounded-md font-medium transition-colors"
                            >
                              <Copy className="w-3 h-3" />
                              Copy JSON-LD ({jsonLd.count})
                            </button>
                          )}
                          {faq.count > 0 && (
                            <button
                              type="button"
                              onClick={copyText(faq.html, faqLabel)}
                              className="text-xs inline-flex items-center gap-1 text-emerald-800 hover:text-emerald-900 bg-emerald-100 hover:bg-emerald-200 px-2 py-1 rounded-md font-medium transition-colors"
                            >
                              <Copy className="w-3 h-3" />
                              Copy FAQ HTML ({faq.count})
                            </button>
                          )}
                          {caps.count > 0 && (
                            <button
                              type="button"
                              onClick={copyText(caps.html, capsLabel)}
                              className="text-xs inline-flex items-center gap-1 text-emerald-800 hover:text-emerald-900 bg-emerald-100 hover:bg-emerald-200 px-2 py-1 rounded-md font-medium transition-colors"
                            >
                              <Copy className="w-3 h-3" />
                              Copy Answer Capsules ({caps.count})
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {jsonLd.count > 0 && (
                      <details className="mb-2">
                        <summary className="cursor-pointer text-xs text-emerald-800 hover:text-emerald-900 font-medium select-none">
                          How to use JSON-LD
                        </summary>
                        <ol className="list-decimal pl-5 mt-2 space-y-1.5 text-xs text-ink-600 bg-emerald-50/60 border border-emerald-100 rounded-lg p-3">
                          <li>
                            Click <strong>Copy JSON-LD</strong> above to copy the{' '}
                            <code className="px-1 py-0.5 bg-white border border-emerald-200 rounded text-[10px]">
                              &lt;script type=&quot;application/ld+json&quot;&gt;
                            </code>{' '}
                            blocks.
                          </li>
                          <li>
                            Paste them just before{' '}
                            <code className="px-1 py-0.5 bg-white border border-emerald-200 rounded text-[10px]">
                              &lt;/head&gt;
                            </code>{' '}
                            in your page&apos;s template (or in your global header partial).
                          </li>
                          <li>
                            Deploy the change to your live site, then re-run the audit —{' '}
                            <strong>JSON-LD schema detected</strong> should now Pass.
                          </li>
                        </ol>
                      </details>
                    )}

                    {faq.count > 0 && (
                      <details className="mb-2">
                        <summary className="cursor-pointer text-xs text-emerald-800 hover:text-emerald-900 font-medium select-none">
                          How to use FAQ HTML
                        </summary>
                        <ol className="list-decimal pl-5 mt-2 space-y-1.5 text-xs text-ink-600 bg-emerald-50/60 border border-emerald-100 rounded-lg p-3">
                          <li>
                            Click <strong>Copy FAQ HTML</strong> above to copy a ready-to-paste{' '}
                            <code className="px-1 py-0.5 bg-white border border-emerald-200 rounded text-[10px]">
                              &lt;section class=&quot;faq&quot;&gt;
                            </code>{' '}
                            block — standard <code className="px-1 py-0.5 bg-white border border-emerald-200 rounded text-[10px]">&lt;h2&gt;</code> / <code className="px-1 py-0.5 bg-white border border-emerald-200 rounded text-[10px]">&lt;h3&gt;</code> / <code className="px-1 py-0.5 bg-white border border-emerald-200 rounded text-[10px]">&lt;p&gt;</code>, no custom tags.
                          </li>
                          <li>
                            Paste it into the visible body of your page (usually near the bottom, before the footer) in your page template or CMS editor.
                          </li>
                          <li>
                            Deploy and re-run the audit — <strong>FAQ sections present</strong> should now Pass.
                          </li>
                        </ol>
                      </details>
                    )}

                    {caps.count > 0 && (
                      <details className="mb-2">
                        <summary className="cursor-pointer text-xs text-emerald-800 hover:text-emerald-900 font-medium select-none">
                          How to use Answer Capsules
                        </summary>
                        <ol className="list-decimal pl-5 mt-2 space-y-1.5 text-xs text-ink-600 bg-emerald-50/60 border border-emerald-100 rounded-lg p-3">
                          <li>
                            Click <strong>Copy Answer Capsules</strong> above to copy{' '}
                            <code className="px-1 py-0.5 bg-white border border-emerald-200 rounded text-[10px]">
                              &lt;blockquote class=&quot;answer-capsule&quot;&gt;
                            </code>{' '}
                            blocks — short standalone facts AI engines can quote verbatim.
                          </li>
                          <li>
                            Paste them into your body content where you want to highlight key facts (often near the top, after the intro paragraph).
                          </li>
                          <li>
                            Deploy and re-run the audit — <strong>Answer Capsules used</strong> should now Pass.
                          </li>
                        </ol>
                      </details>
                    )}
                  </>
                );
              })()}
              <pre className="text-xs bg-emerald-50 border border-emerald-200 rounded-xl p-3 max-h-80 overflow-auto whitespace-pre-wrap scroll-soft">
                {(selectedPage.optimized_content || '').slice(0, 4000) || '(none)'}
              </pre>
            </div>
          </div>

          {!!selectedPage.versions?.length && (
            <div className="mt-5">
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <History className="w-4 h-4 text-ink-400" />
                Version history ({selectedPage.versions.length})
              </h4>
              <div className="divide-y divide-gray-50 border border-gray-100 rounded-xl overflow-hidden">
                {[...selectedPage.versions]
                  .slice()
                  .reverse()
                  .map((v, idx) => (
                    <div
                      key={v._id || idx}
                      className="flex items-center justify-between text-sm px-3 py-2.5 hover:bg-gray-50/60"
                    >
                      <div className="min-w-0">
                        <span className="pill bg-gray-100 text-gray-700 mr-2">
                          {v.label || 'version'}
                        </span>
                        <span className="text-ink-700">
                          {v.enhancements?.faqs ?? 0} FAQs ·{' '}
                          {v.enhancements?.answerCapsules ?? 0} caps
                        </span>
                        <span className="text-ink-400 ml-2 text-xs">
                          {formatRelative(v.createdAt)}
                        </span>
                      </div>
                      <button
                        className="text-brand-600 hover:underline text-xs inline-flex items-center gap-1"
                        onClick={() =>
                          restoreVersionMutation.mutate({
                            pageId: selectedPage._id,
                            content: v.content,
                            label: `restore_${v.label || 'version'}`,
                          })
                        }
                      >
                        <RotateCcw className="w-3 h-3" /> Restore
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </Dialog>
      )}
    </div>
  );
}
